import _ from '@lodash'
import {
  useHotelStore,
  useTemplatesStore,
} from 'pinia-store'
import { reactive } from 'vue'
import { useRoute } from 'vue-router'
import { stripHtml } from '@general-function'
import { toQueryUrl } from '@ui/composables'
import { useSeoMeta } from '#imports'

interface ContentProps {
  page_title: string
  page_subtitle: string
  page_image: string
  page_meta_title: string
  page_meta_description: string
  title: string
  description: string
  slug: string
}
export function useHeadMeta(content: ContentProps) {
  const route = useRoute()
  const useHotel = useHotelStore()
  const useTemplates = useTemplatesStore()
  const hotel = computed(() => useHotel.getProfile)

  const page_description = content?.description || content?.page_meta_description || hotel.value?.m_description

  let host = import.meta.env.VITE_APP_URL
  if (typeof window !== 'undefined' && !host)
    host = window.location.hostname

  const DEFAULT_CONTENT = {
    title: hotel.value.hotel_name,
    titleTemplate: '',
    charset: 'utf-8',
    themeColor: useTemplates.detail?.primary.background_color || '#ffffff',
    viewport: 'width=device-width, initial-scale=1',
    meta: [
      { property: 'og:site_name', content: _.capitalize(hotel.value.hotel_name) },
      { hid: 'og:type', property: 'og:type', content: 'website' },
    ],
    bodyAttrs: {
      class: '',
    },
    link: [
      { rel: 'canonical', href: host },
    ],
  }
  const head = reactive(DEFAULT_CONTENT)
  const init = () => {
    const {
      value,
    }: any = hotel
    if (value && value.hotel_name) {
      head.title = value.hotel_name
      head.titleTemplate = hotel.value.hotel_name
    }
    else {
      head.title = value.hotel_name
      head.titleTemplate = import.meta.env.VITE_HOTEL_NAME
    }

    if (content && content.page_title) {
      head.title = content.page_title
      head.titleTemplate += ` | ${content.page_title}`
    }
    else if (content && content.page_meta_title) {
      head.title = content.page_meta_title
      head.titleTemplate += ` | ${content.page_meta_title}`
    }
    else if (content && content.title) {
      head.title = content.title
      head.titleTemplate += ` | ${content.title}`
    }
    else if (route.name) {
      head.title = _.capitalize(route.name)
      head.titleTemplate += ` | ${_.capitalize(route.name)}`
    }
    if (content && Object.keys(content).length && head.meta.length && value
    && value.hotel_name) {
      // eslint-disable-next-line array-callback-return
      head.meta.map((el: any) => {
        if (el?.property === 'og:site_name' && value.hotel_name)
          el.content = value.hotel_name
      })
    }
    if (route?.params?.language)
      head.htmlAttrs = route.params.language
    head.bodyAttrs.class = `${String(import.meta.env.VITE_APP_HOTEL_NAME)}-${String(route.name)}`
    const index = head.link.findIndex(el => el.rel === 'icon')
    if (value && value.hotel_favicon && index === -1) {
      head.link.push({ rel: 'icon', href: value.hotel_favicon })
    }
    else if (value && value.hotel_logo && index === -1) {
      head.link.push({
        rel: 'icon',
        href: toQueryUrl(value.hotel_logo, {
          d: '32x32',
        }),
      })
    }
  }

  onMounted(() => {
    init()
  })

  watchEffect(() => {
    useSeoMeta({
      title: () => head.title,
      ogUrl: () => `${host}${route.path}`,
      ogSiteName: () => hotel.value?.hotel_name
        ? hotel.value.hotel_name
        : '',
      ogTitle: () => head.title,
      description: () => route.name === 'general-terms-slug'
        ? hotel.value?.m_description
        : stripHtml(page_description as string, false),
      ogDescription: () => route.name === 'general-terms-slug'
        ? hotel.value?.m_description
        : stripHtml(page_description as string, false),
      ogImage: () => hotel.value?.thumbnail_desktop
        ? toQueryUrl(hotel.value.thumbnail_desktop, {
          d: '1500x1000',
        })
        : '',
      twitterCard: () => 'summary_large_image',
    })
    useHead({
      ...head,
      bodyAttrs: {
        class: 'data-home', // !important
      },
    })
  })
}
